import { createSelector } from 'reselect';

import intersection from 'lodash.intersection';
import { normalize } from 'normalizr';
import dayjs from 'utils/dayjs';

import {
  getCurrencyTypeInURLFormat,
  getURIByLocationWithParams,
  isNumber,
  DEFAULT_API_DATE_FORMAT,
  DEFAULT_CLIENT_DATE_FORMAT,
  DEFAULT_TIMESTAMP_FORMAT,
  ROUTER_PARAMS_CONFIG,
  DEFAULT_MOBILE_DATE_FORMAT,
  TYPES_ENUM,
  filterExcludedCurrencies,
  filterExcludedLinks,
} from 'utils';

import { convertorBanksRates as banksRatesSchema } from 'schemas';

import { parse } from 'query-string';

export const getBanks = state => state.convertorBanks;

const getSearchSubstitution = ({ from = '', to = '' }) => {
  const [sourceCurrency, sourceType] = from.split('-');
  const [targetCurrency, targetType] = to.split('-');

  const result = {
    sourceCurrency,
    sourceType: sourceType && `-${sourceType}`,
    targetCurrency,
    targetType: targetType && `-${targetType}`,
  };

  return Object.keys(result).reduce((acc, key) => {
    const value = result[key];

    if (value) {
      acc[key] = value;
    }

    return acc;
  }, {});
};

export const getCurrenciesCrossRates = state =>
  state.convertorCurrenciesCrossRates;

const getLocationSearch = (state, props) => {
  return props?.location?.search || '';
};
const getPathParams = (state, props) => {
  return props?.match?.params || {};
};

export const makeGetLocationState = () => {
  return createSelector(
    getLocationSearch,
    getPathParams,
    (locationSearch, pathParams) => {
      const queries = parse(locationSearch);

      return {
        ...pathParams,
        ...queries,
        ...getSearchSubstitution(queries),
      };
    }
  );
};

export const makeGetLocationParamsValues = () => {
  const getLocationState = makeGetLocationState();

  return createSelector(getLocationState, locationState =>
    Object.keys(ROUTER_PARAMS_CONFIG).reduce((result, paramId) => {
      const paramConfig = ROUTER_PARAMS_CONFIG[paramId];

      if (!paramConfig) {
        return result;
      }

      const { getFormattedValue, urlName } = paramConfig;

      return {
        ...result,
        [paramId]: getFormattedValue(locationState[urlName]),
      };
    }, {})
  );
};

export const getLocationParamsValues = makeGetLocationParamsValues();

export const makeGetCurrenciesCrossRatesRequestKey = () =>
  createSelector(
    makeGetLocationParamsValues(),
    getLocationSearch,
    ({ sourceCurrency, targetCurrency }, locationSearch) => {
      const {
        'converter-type': converterType = 'midbank',
        'currencies-list': currenciesList = '',
        date,
      } = parse(locationSearch);

      const requestUrlParamsCurrenciesList = currenciesList
        ? currenciesList
            .split('-')
            .reduce((result, item) => `${result}cur_list[]=${item}&`, '')
        : '';

      const requestUrlParamsBase =
        sourceCurrency && targetCurrency
          ? `currency1=${sourceCurrency}&currency2=${targetCurrency}`
          : `currency1=usd&currency2=uah`;

      const requestUrlParamsType =
        converterType && converterType !== 'nbu'
          ? `converter_type=${converterType}`
          : '';

      return `${requestUrlParamsCurrenciesList}${requestUrlParamsBase}&${requestUrlParamsType}${
        date ? `date=${date}` : ''
      }`;
    }
  );

export const getCurrenciesCrossRatesRequestKey = makeGetCurrenciesCrossRatesRequestKey();

export const makeGetCurrenciesCrossRatesByKey = () =>
  createSelector(
    getCurrenciesCrossRates,
    getCurrenciesCrossRatesRequestKey,
    (crossRates, requestKey) => crossRates[requestKey] || {}
  );

export const getCurrenciesCrossRatesByKey = makeGetCurrenciesCrossRatesByKey();

export const makeGetCurrenciesCrossRatesItems = () =>
  createSelector(getCurrenciesCrossRatesByKey, byKey => byKey.rates);

export const getCurrenciesCrossRatesItems = makeGetCurrenciesCrossRatesItems();

export const makeGetCurrenciesCrossRatesIsFetching = () =>
  createSelector(
    getCurrenciesCrossRatesByKey,
    byKey => byKey.isFetching || false
  );

export const getCurrenciesCrossRatesIsFetching = makeGetCurrenciesCrossRatesIsFetching();

export const makeGetCurrenciesCrossRatesIsLoaded = () =>
  createSelector(
    getCurrenciesCrossRatesByKey,
    byKey => byKey.isLoaded || false
  );

export const getCurrenciesCrossRatesIsLoaded = makeGetCurrenciesCrossRatesIsLoaded();

export const makeGetCurrenciesCrossRatesHasData = () =>
  createSelector(getCurrenciesCrossRatesItems, rates =>
    Boolean(rates?.sell && Object.keys(rates.sell).length > 0)
  );

export const getCurrenciesCrossRatesHasData = makeGetCurrenciesCrossRatesHasData();

const getCrossRateSellValue = (rates, code) => {
  if (!rates || !code) {
    return undefined;
  }

  if (rates[code] != null) {
    return rates[code];
  }

  return rates[code.toUpperCase()];
};

export const getCurrenciesInfo = state => state.convertorCurrenciesInfo;

export const getIsCurrenciesInfoInitialized = createSelector(
  getCurrenciesInfo,
  currenciesInfo => currenciesInfo.isInitialized
);

export const getIsCurrenciesInfoFetching = createSelector(
  getCurrenciesInfo,
  currenciesInfo => currenciesInfo.isFetching
);

export const getCurrenciesInfoIds = createSelector(
  getCurrenciesInfo,
  currenciesInfo => currenciesInfo.ids
);

export const getCurrenciesInfoEntities = createSelector(
  getCurrenciesInfo,
  currenciesInfo => currenciesInfo.entities
);

export const getCurrenciesInfoEntitiesWithCodeKey = createSelector(
  getCurrenciesInfoEntities,
  entities =>
    Object.values(entities).reduce(
      (res, entity) => ({ ...res, [entity.code]: entity }),
      {}
    )
);

export const getSelectedCurrencies = createSelector(
  getCurrenciesCrossRatesItems,
  getCurrenciesInfoEntitiesWithCodeKey,
  (ratesItems, currenciesEntities) =>
    Object.keys(ratesItems?.sell || {})
      .filter(currencyCode => {
        const entity =
          currenciesEntities[currencyCode] ||
          currenciesEntities[currencyCode.toUpperCase()];

        return Boolean(entity);
      })
      .map(currencyCode => {
        const { code, name, type } =
          currenciesEntities[currencyCode] ||
          currenciesEntities[currencyCode.toUpperCase()];

        return { code, name, type };
      })
);

export const makeGetCurrenciesPairInfo = () =>
  createSelector(
    getCurrenciesInfoIds,
    getCurrenciesInfoEntities,
    makeGetLocationParamsValues(),
    (
      ids,
      entities,
      { sourceCurrency, targetCurrency, sourceType, targetType }
    ) => {
      const sourceId = ids.find(id => {
        const { slug, type } = entities[id];

        return slug.toLowerCase() === sourceCurrency && type === sourceType;
      });
      const targetId = ids.find(id => {
        const { slug, type } = entities[id];

        return slug.toLowerCase() === targetCurrency && type === targetType;
      });

      return {
        source: entities[sourceId] || {},
        target: entities[targetId] || {},
      };
    }
  );

export const getCurrenciesPairInfo = makeGetCurrenciesPairInfo();
export const getCurrenciesPairInfoForHooks = makeGetCurrenciesPairInfo();

export const getCurrenciesInfoForLinksSection = createSelector(
  getCurrenciesInfoIds,
  getCurrenciesInfoEntities,
  getLocationParamsValues,
  (ids, entities, { sourceCurrency, sourceType }) =>
    ids.reduce((result, id) => {
      const { group, slug, type } = entities[id];

      const lowerSlug = slug.toLowerCase();

      if (
        (group === 'base' || group === 'priority') &&
        (sourceCurrency !== lowerSlug ||
          (sourceCurrency === lowerSlug && sourceType !== type))
      ) {
        return [...result, entities[id]];
      }

      return result;
    }, [])
);

export const getSortedCurrenciesInfoForLinksSection = createSelector(
  getCurrenciesInfoForLinksSection,
  currenciesInfo =>
    filterExcludedCurrencies(currenciesInfo).sort(
      ({ group: groupA }, { group: groupB }) => {
        if (groupA === 'priority' && groupB === 'base') {
          return -1;
        }

        if (groupA === 'base' && groupB === 'priority') {
          return 1;
        }

        return 0;
      }
    )
);

export const getCurrenciesInfoWithRates = createSelector(
  getSortedCurrenciesInfoForLinksSection,
  getCurrenciesCrossRatesItems,
  getCurrenciesPairInfo,
  (sortedCurrenciesInfo, crossRatesItems, { source, target }) =>
    sortedCurrenciesInfo.reduce((result, item) => {
      const { group, code } = item;
      const baseRate = getCrossRateSellValue(
        crossRatesItems?.sell,
        source.code
      );
      const rate = getCrossRateSellValue(crossRatesItems?.sell, code);

      if (
        (group === 'priority' || group === 'base') &&
        code !== source.code &&
        code !== target.code
      ) {
        result.push({
          ...item,
          rate: baseRate === 0 || rate === 0 ? 0 : baseRate / rate,
        });
      }

      return result;
    }, [])
);

const PRIORITY_PRIORITY_REGEXP = /^((\d{1,3})|(\d{3}(0|9){1,2})|(\d{3}0{3})|(\d{2}0{5})|(\d0{7})|(\d0{9}))$/;
const PRIORITY_BASE_REGEXP = /^((\d{1,3})|(\d{3}0)|(\d{3}0{2})|(\d{2}0{4})|(\d0{6,7})|(\d0{9}))$/;
const PRIORITY_ADDITIONAL_REGEXP = /^((\d{1,2})|(\d{2}0{1,3})|(\d0{5,7})|(\d0{9}))$/;
const BASE_BASE_REGEXP = /^\d0{0,4}$/;
const ADDITIONAL_ADDITIONAL_REGEXP = /^1$/;
const BASE_ADDITIONAL_REGEXP = /^\d0{0,2}$/;

const GROUPS_REGEXP = {
  'priority-priority': PRIORITY_PRIORITY_REGEXP,
  'base-base': BASE_BASE_REGEXP,
  'additional-additional': ADDITIONAL_ADDITIONAL_REGEXP,
  'priority-base': PRIORITY_BASE_REGEXP,
  'base-priority': PRIORITY_BASE_REGEXP,
  'priority-additional': PRIORITY_ADDITIONAL_REGEXP,
  'additional-priority': PRIORITY_ADDITIONAL_REGEXP,
  'base-additional': BASE_ADDITIONAL_REGEXP,
  'additional-base': BASE_ADDITIONAL_REGEXP,
};

export const getPageIsHidingFromCrawlers = createSelector(
  getCurrenciesPairInfo,
  (state, props) => props.match.params,
  ({ source, target }, { val1: value }) => {
    const sourceGroup = source.group;
    const targetGroup = target.group;

    const regexp = GROUPS_REGEXP[`${sourceGroup}-${targetGroup}`];

    return value && regexp ? !regexp.test(value) : false;
  }
);

export const getLocationParamsValuesForHooks = makeGetLocationParamsValues();

export const getBanksInitialized = createSelector(
  getBanks,
  banks => banks.isInitialized
);

export const getBanksFetching = createSelector(
  getBanks,
  banks => banks.isFetching
);

export const getBanksIds = createSelector(getBanks, banks => banks.ids);

export const getBanksEntities = createSelector(
  getBanks,
  banks => banks.entities
);

export const getBanksItems = createSelector(
  getBanksIds,
  getBanksEntities,
  (ids, entities) => {
    return ids.map(id => {
      return entities[id];
    });
  }
);

const converterTypes = [
  'midbank',
  'bestbank',
  'auction',
  'nbu',
  'contractsBank',
];

const filterConverterTypes = (types, typesIds = converterTypes) =>
  typesIds.filter(
    id => types[id] && types[id].val && parseFloat(types[id].val)
  );

const sortRates = rates =>
  rates.sort(
    ({ val: val1 }, { val: val2 }) => parseFloat(val1) - parseFloat(val2)
  );

const getBanksRates = rates => {
  return Object.values(rates).filter(rate => !!rate.companyId);
};

const getCurrenciesPairTimestamps = (source, target, date, converterType) => {
  return {
    sourceTimestamp: source?.sell?.[converterType]?.timestamp || date,
    targetTimestamp: target?.buy?.[converterType]?.timestamp || date,
  };
};

const getTimestampByConverterType = (
  sourceTimestamp,
  targetTimestamp,
  date
) => {
  const sTimestamp = sourceTimestamp || date;
  const tTimestamp = targetTimestamp || date;

  const sourceFormatRules =
    sTimestamp === date ? DEFAULT_API_DATE_FORMAT : DEFAULT_TIMESTAMP_FORMAT;

  const targetFormatRules =
    tTimestamp === date ? DEFAULT_API_DATE_FORMAT : DEFAULT_TIMESTAMP_FORMAT;

  const formattedSourceTimestamp = dayjs(sTimestamp, sourceFormatRules);

  const formattedTargetTimestamp = dayjs(tTimestamp, targetFormatRules);

  return formattedSourceTimestamp.isAfter(formattedTargetTimestamp)
    ? formattedSourceTimestamp.format(DEFAULT_CLIENT_DATE_FORMAT)
    : formattedTargetTimestamp.format(DEFAULT_CLIENT_DATE_FORMAT);
};

export const getCurrencyRates = state => state.convertorCurrencyRates;

export const getCurrencyRatesIsFetching = createSelector(
  getCurrencyRates,
  ({ isFetching }) => isFetching
);

export const makeGetCurrencyRatesByParams = () =>
  createSelector(
    getCurrencyRates,
    makeGetLocationParamsValues(),
    (
      currencyRates,
      { date, sourceType, targetType, sourceCurrency, targetCurrency }
    ) => {
      return {
        source:
          currencyRates?.[date]?.[
            `${sourceCurrency}${getCurrencyTypeInURLFormat(sourceType)}`
          ] || null,
        target:
          currencyRates?.[date]?.[
            `${targetCurrency}${getCurrencyTypeInURLFormat(targetType)}`
          ] || null,
      };
    }
  );

export const getCurrencyRatesByParams = makeGetCurrencyRatesByParams();

export const makeGetSortedBanksRates = () =>
  createSelector(getCurrencyRatesByParams, ({ source, target }) => {
    const sourceRates = source?.sell || {};
    const targetRates = target?.buy || {};

    return {
      sourceBanksRates: sortRates(getBanksRates(sourceRates)),
      targetBanksRates: sortRates(getBanksRates(targetRates)),
    };
  });

const getSortedBanksRates = makeGetSortedBanksRates();

export const getBanksRatesWithInfo = createSelector(
  getSortedBanksRates,
  getBanksEntities,
  getLocationParamsValues,
  (
    { sourceBanksRates, targetBanksRates },
    banksEntities,
    { sourceValue, targetType, date }
  ) => {
    const {
      entities: { banksRates: sourceEntities = {} },
      result: sourceIds,
    } = normalize(sourceBanksRates, banksRatesSchema);

    const {
      entities: { banksRates: targetEntities = {} },
    } = normalize(targetBanksRates, banksRatesSchema);

    const rates = sourceIds.reduce((result, sourceId) => {
      const sourceEntity = sourceEntities[sourceId];
      const targetEntity = targetEntities[sourceId];

      if (!targetEntity) {
        return result;
      }

      const rate = parseFloat(sourceEntity.val) / parseFloat(targetEntity.val);

      const timestamp = getTimestampByConverterType(
        sourceEntity.timestamp,
        targetEntity.timestamp,
        date
      );

      return [
        ...result,
        {
          ...(banksEntities[sourceEntity.companyId] || {}),
          rate,
          summ: sourceValue * rate,
          type: targetType,
          timestamp,
        },
      ];
    }, []);

    return rates;
  }
);

export const getConverterTypesOptions = createSelector(
  getCurrencyRatesByParams,
  (state, props) => props.intl,
  ({ source, target }, intl) => {
    const sourceTypes = source?.sell || {};
    const targetTypes = target?.buy || {};

    const filteredSourceTypes = filterConverterTypes(sourceTypes);
    const filteredTargetTypes = filterConverterTypes(targetTypes);

    const intersectionTypes = intersection(
      filteredSourceTypes,
      filteredTargetTypes
    );

    return intersectionTypes.map(id => ({
      id,
      label: intl.formatMessage({
        id: `currencyConverter.converterType.${id}`,
      }),
      value: id,
    }));
  }
);

export const getExistedConverterType = createSelector(
  getConverterTypesOptions,
  getLocationParamsValues,
  (converterTypesOptions, { converterType }) => {
    const isTypeExist = converterTypesOptions.some(
      ({ value }) => value === converterType
    );

    return isTypeExist ? converterType : converterTypesOptions[0]?.value;
  }
);

export const makeGetCurrencyRatesByConverterType = () =>
  createSelector(
    getCurrencyRatesByParams,
    getExistedConverterType,
    (currencyRates, converterType) => {
      return {
        sourceRate: currencyRates?.source?.sell?.[converterType]?.val || null,
        targetRate: currencyRates?.target?.buy?.[converterType]?.val || null,
      };
    }
  );

export const makeGetCurrencyRatesByCurrencyPair = () =>
  createSelector(
    makeGetCurrencyRatesByConverterType(),
    makeGetLocationParamsValues(),
    (
      { sourceRate: source, targetRate: target },
      { sourceValue, targetValue, priority }
    ) => {
      const intSourceRate = parseFloat(source);
      const intTargetRate = parseFloat(target);
      const sourceRate = intSourceRate / intTargetRate;
      const targetRate = intTargetRate / intSourceRate;

      return {
        sourceRate,
        targetRate,
        sourceValue:
          priority === 0
            ? sourceValue
            : parseFloat(targetValue === '' ? 0 : targetValue) * targetRate,
        targetValue:
          priority === 1
            ? targetValue
            : parseFloat(sourceValue === '' ? 0 : sourceValue) * sourceRate,
      };
    }
  );

export const getCurrencyRatesByCurrencyPair = makeGetCurrencyRatesByCurrencyPair();

export const getTimestamp = createSelector(
  getCurrencyRatesByParams,
  getLocationParamsValues,
  ({ source, target }, { date, converterType }) => {
    const { sourceTimestamp, targetTimestamp } = getCurrenciesPairTimestamps(
      source,
      target,
      date,
      converterType
    );

    return getTimestampByConverterType(sourceTimestamp, targetTimestamp, date);
  }
);

export const makeGetCurrencyAllRates = () => {
  const ratesTypes = [
    'auction',
    'midbank',
    'interbank',
    'nbu',
    'contractsBank',
    'visa',
    'mastercard',
  ];

  return createSelector(
    getCurrencyRatesByParams,
    getLocationParamsValues,
    ({ source, target }, { sourceValue, targetType, date }) => {
      return ratesTypes.map(type => {
        const sourceRate = source?.sell?.[type]?.val;
        const targetRate = target?.buy?.[type]?.val;

        const rate = sourceRate / targetRate;

        const {
          sourceTimestamp,
          targetTimestamp,
        } = getCurrenciesPairTimestamps(source, target, date, type);

        const timestamp = getTimestampByConverterType(
          sourceTimestamp,
          targetTimestamp,
          date
        );

        return {
          id: type,
          rate: isNumber(rate) ? rate : '-',
          summ: isNumber(rate) ? rate * sourceValue : '-',
          type: targetType,
          timestamp,
        };
      });
    }
  );
};

export const getExchangeRateDynamics = state =>
  state.convertorExchangeRateDynamics;

export const makeGetMonthlyExchangeRateDynamics = () =>
  createSelector(
    getExchangeRateDynamics,
    makeGetCurrenciesPairInfo(),
    (exchangeRateDynamics, { source, target }) =>
      exchangeRateDynamics[`${source.slug}-${target.slug}`]?.month || null
  );

export const getMonthlyExchangeRateDynamics = makeGetMonthlyExchangeRateDynamics();

export const makeGetYearlyExchangeRateDynamics = () =>
  createSelector(
    getExchangeRateDynamics,
    makeGetCurrenciesPairInfo(),
    (exchangeRateDynamics, { source, target }) =>
      exchangeRateDynamics[`${source.slug}-${target.slug}`]?.year || null
  );

export const getYearlyExchangeRateDynamics = makeGetYearlyExchangeRateDynamics();

const normalizeExchangeRateDynamicsData = (
  data,
  value,
  type,
  inputDateFormat,
  outputDateFormat,
  locale
) => {
  return (data || [])
    .map(({ date, course: rate }) => ({
      id: date,
      rate,
      type,
      summ: value * rate,
      timestamp: dayjs(date, inputDateFormat)
        .locale(locale)
        .format(outputDateFormat),
    }))
    .reverse();
};

export const getMonthlyNormalizedExchangeRateDynamicsData = createSelector(
  getMonthlyExchangeRateDynamics,
  getCurrenciesPairInfo,
  getLocationParamsValuesForHooks,
  (state, props) => props.intl.locale,
  (exchangeRateDynamics, { source }, { sourceValue }, locale) => {
    return normalizeExchangeRateDynamicsData(
      exchangeRateDynamics,
      sourceValue,
      source.type,
      DEFAULT_MOBILE_DATE_FORMAT,
      'DD MMMM, ddd',
      locale
    );
  }
);

export const getYearlyNormalizedExchangeRateDynamicsData = createSelector(
  getYearlyExchangeRateDynamics,
  getCurrenciesPairInfo,
  getLocationParamsValuesForHooks,
  (state, props) => props.intl.locale,
  (exchangeRateDynamics, { source }, { sourceValue }, locale) => {
    return normalizeExchangeRateDynamicsData(
      exchangeRateDynamics,
      sourceValue,
      source.type,
      'YYYY-MM',
      'MMMM YYYY',
      locale
    );
  }
);

export const getCurrencies = state => state.convertorCurrencies;

export const getCurrenciesFetching = createSelector(
  getCurrencies,
  currencies => currencies.isFetching
);

export const getCurrenciesItems = createSelector(
  getCurrencies,
  currencies => currencies.currenciesItems
);

export const getConvertorRatesGraph = state => state.convertorRatesGraph;

export const getConvertorRatesGraphParams = (state, params) => params;

export const getIsConvertorRatesGraphFetching = createSelector(
  getConvertorRatesGraph,
  ratesGraph => ratesGraph.isFetching
);

export const makeGetConvertorRatesGraphByKeys = () =>
  createSelector(
    getConvertorRatesGraph,
    getConvertorRatesGraphParams,
    (rates, { sourceCurrency, targetCurrency, period }) =>
      rates[`${sourceCurrency}-${targetCurrency}`]?.[period]
  );

export const makeGetConvertorRatesGraphIsFetching = () => {
  const getConvertorRatesGraphByKeys = makeGetConvertorRatesGraphByKeys();

  return createSelector(
    getConvertorRatesGraphByKeys,
    rates => rates?.isFetching || false
  );
};

export const makeGetConvertorRatesGraphIsInitialized = () => {
  const getConvertorRatesGraphByKeys = makeGetConvertorRatesGraphByKeys();

  return createSelector(
    getConvertorRatesGraphByKeys,
    rates => rates?.isInitialized || false
  );
};

export const makeGetConvertorRatesGraphItems = () => {
  const getConvertorRatesGraphByKeys = makeGetConvertorRatesGraphByKeys();

  return createSelector(getConvertorRatesGraphByKeys, rates =>
    (rates?.ids || []).map(id => rates.entities[id])
  );
};

export const getConvertorRatesGraphItems = makeGetConvertorRatesGraphItems();
export const getConvertorRatesGraphIsFetchingForHooks = makeGetConvertorRatesGraphIsFetching();
export const getConvertorRatesGraphIsInitializedForHooks = makeGetConvertorRatesGraphIsInitialized();

export const getConvertorRatesGraphTimeLine = createSelector(
  getConvertorRatesGraphItems,
  getConvertorRatesGraphParams,
  (ratesItems, { period, locale }) =>
    ratesItems.map(({ date }) =>
      dayjs(date, DEFAULT_MOBILE_DATE_FORMAT)
        .locale(locale)
        .format(period === '1826' ? 'MMM YY' : 'D MMM')
    )
);

export const getConvertorRatesMatrix = createSelector(
  getConvertorRatesGraphItems,
  ratesItems =>
    ratesItems.reduce(
      (result, item) => {
        Object.entries(item.course).forEach(([key, course]) => {
          if (course.ask && typeof course.ask === 'number') {
            result[TYPES_ENUM[key]].push(course.ask);
          } else if (typeof course === 'number') {
            result[TYPES_ENUM[key]].push(course);
          }
        });

        return result;
      },
      [[], [], [], []]
    )
);

export const getLinksState = state => {
  return state.convertorLinks;
};

export const getLinksIdsMap = createSelector(
  getLinksState,
  convertorLinks => convertorLinks.idsMap
);

export const getLinksEntities = createSelector(
  getLinksState,
  convertorLinks => convertorLinks.entities
);

export const getIsLinksDataFetching = createSelector(
  getLinksState,
  convertorLinks => convertorLinks.isFetching
);

export const makeGetIsLinksDataInitialized = () =>
  createSelector(
    getLinksIdsMap,
    (state, props) => props.location,
    (state, props) => props.locale || props.intl?.locale,
    (map, location, locale) => {
      if (!location || !locale) {
        return false;
      }

      const url = getURIByLocationWithParams(location, locale);

      return Object.prototype.hasOwnProperty.call(map, url);
    }
  );

export const getIsLinksDataInitialized = makeGetIsLinksDataInitialized();

export const makeGetLinksIdsByUrl = () => {
  return createSelector(
    getLinksIdsMap,
    (state, props) => props.location,
    (state, props) => props.locale || props.intl.locale,
    (map, location, locale) => {
      return map[getURIByLocationWithParams(location, locale)] || [];
    }
  );
};

const getLinksIdsByUrl = makeGetLinksIdsByUrl();

export const makeGetLinksItems = () => {
  return createSelector(getLinksIdsByUrl, getLinksEntities, (ids, entities) => {
    return ids.map(id => ({
      id,
      name: entities[id].name,
      url: entities[id].url,
      rel: entities[id].rel,
      active: entities[id].active,
      links: entities[id].children.map(link => ({
        id: link.id,
        name: link.name,
        url: link.url,
        rel: link.rel,
        active: link.active,
      })),
    }));
  });
};

export const getLinksItems = makeGetLinksItems();

export const getActiveLinksItems = createSelector(getLinksItems, linksItems => {
  return linksItems.map(item => ({
    ...item,
    links: filterExcludedLinks(item.links.filter(link => link.active)),
  }));
});
